import { EnvironmentType } from '@feedyou/schema'
import { isEnabled, Version } from '@feedyou/utils'

import { FeedbotThunk } from '../../../store/types'
import { botFlowSelector } from '../../../store/flow/selectors/botFlowSelector'
import { accessLevelSelector } from '../../../store/user'
import { feedbotToast } from '../../../lib/feedbotToast'
import { confirm, dialog } from '../../../lib/bootbox'

import { removePrivilegedActions } from '../utils/bootbox'
import { botMetadataForEnvironmentSelector } from '../selectors/botMetadataForEnvironmentSelector'
import { currentBotEnvironmentGroupIdSelector } from '../selectors/currentBotEnvironmentGroupIdSelector'
import { endpointHelpers } from '../endpointHelpers'
import { noop } from 'lodash'
import { environmentGroupForCurrentBotSelector } from '@features/serverBots/selectors/environmentGroupForCurrentBotSelector'
import { EnvironmentGroupDescriptor } from '@features/serverBots/types'
import { switchToBotIfCurrentUserCanViewIt } from '@features/serverBots/actions/switchToBotIfCurrentUserCanViewIt'

export const navigateToEnv =
  (targetEnv: EnvironmentType): FeedbotThunk =>
  async (dispatch, getState) => {
    const currentBotVersion = botFlowSelector(getState())?.version
    const accessLevel = accessLevelSelector(getState())
    const currentBotEnvGroupId = currentBotEnvironmentGroupIdSelector(getState())
    const targetMetadata = botMetadataForEnvironmentSelector(
      currentBotEnvGroupId,
      targetEnv
    )(getState())

    const targetId = targetMetadata?.id

    if (!targetId) {
      return feedbotToast.error({
        title: 'Failed to switch environments',
        content: `We could not find a ${targetEnv} environment for current bot`
      })
    }

    if (isEnabled(targetMetadata)) {
      dispatch(switchToBotIfCurrentUserCanViewIt(targetId))
      return
    }

    if (targetEnv === 'prod') {
      const envGroupDescriptor = environmentGroupForCurrentBotSelector(getState())
      if (await confirmCopyingCurrentBotToProduction(envGroupDescriptor)) {
        await dispatch(endpointHelpers.moveTestToProd.initiate())
          .unwrap()
          .then(() => dispatch(switchToBotIfCurrentUserCanViewIt(targetId)))
          .catch(noop)
        dispatch(switchToBotIfCurrentUserCanViewIt(targetId))
        return
      }
    }

    if (targetEnv === 'test') {
      const envGroupDescriptor = environmentGroupForCurrentBotSelector(getState())
      if (
        await confirmEnablingDevEnv(currentBotVersion!, !!accessLevel.copywrite, envGroupDescriptor)
      ) {
        await dispatch(endpointHelpers.moveProdToTest.initiate())
          .unwrap()
          .then(() => dispatch(switchToBotIfCurrentUserCanViewIt(targetId)))
          .catch(noop)

        return
      }
    }
  }

export const confirmCopyingCurrentBotToProduction = ({ prodName }: EnvironmentGroupDescriptor) => {
  return confirm({
    title: '️⚠️ Your virtual assistant is not live yet',
    message: `There is no bot version in the ${prodName} environment yet. However, you can use the "Copy to ${prodName}" button to push the current one.`,
    buttons: {
      confirm: { label: `Copy to ${prodName}`, className: 'btn-positive' }
    }
  })
}

export async function confirmEnablingDevEnv(
  currentVersion: string,
  canCopywrite: boolean,
  { testName }: EnvironmentGroupDescriptor
): Promise<boolean> {
  return new Promise((res, rej) => {
    const basicOptions = {
      title: `${testName} environment disabled`,
      onEscape: () => res(false),
      callback: () => res(false),
      message: `There is no active version in ${testName} environment, do you want to create there new major version ${Version.incrementMajor(
        currentVersion
      )}?`,
      size: 'medium',
      buttons: {
        close: { label: 'Close', callback: () => res(false), className: 'btn-secondary' },
        ok: { label: `Enable ${testName}`, callback: () => res(true), className: 'btn-positive' }
      }
    }

    const options = canCopywrite ? basicOptions : removePrivilegedActions(basicOptions, testName)

    dialog(options)
  })
}
